import PropTypes from "prop-types";
import * as React from "react";
import { composerReducer } from "../../../domain/productComposer/composer";
import {
  createEditComposer,
  editProblems,
  isReadOnlyFor,
  toUpdateRequest,
} from "../../../domain/productComposer/edit";
import { fixedBumpControlsFor } from "../../../domain/productComposer/fixedBumpControls";
import SignType from "../../../domain/signType";
import { updateProduct } from "../../../services/productComposer";
import { useNotification } from "../../../services/Notifications";
import { Alert, Button, SectionHead, Typography } from "../../../themeV2";
import ComposerShell from "../ComposerShell";
import ConfirmDialog from "../ConfirmDialog";
import useComposerClose from "../useComposerClose";
import ImageEditor from "../images/ImageEditor";
import UploadStatus from "../images/UploadStatus";
import useBatchUploader from "../images/useBatchUploader";
import ImagesSection from "../sections/ImagesSection";
import DiscardDialog from "../DiscardDialog";
import AdvancedSection from "../sections/AdvancedSection";
import DetailsSection from "../sections/DetailsSection";
import ProblemList from "../sections/ProblemList";
import ServicesSection from "../sections/ServicesSection";
import SignTypePricing from "../sections/SignTypePricing";
import TagsSection from "../sections/TagsSection";
import css from "./edit.module.css";

const zeroSplitsOf = (state) =>
  state.signers
    .filter((signer) => (signer.splits[state.signedTypes[0]] ?? 0) === 0)
    .map((signer) => signer.name);

const productFacts = ({
  product,
  productType,
  settings,
  otherBumpPercentage,
  otherImageRequirements,
}) => ({
  bumpFacts: {
    bumpPercentage:
      productType?.bumpPercentage ??
      product.bumpPercentage ??
      otherBumpPercentage ??
      0,
    additionalBumpAmount: product.additionalBumpAmount ?? 0,
    fixedBump: settings.fixedBump,
    productBump: settings.productBump,
  },
  aspectRatio: productType?.aspectRatio ?? product.productTypeAspectRatio ?? 1,
  requirements: productType ?? otherImageRequirements,
});

export default function EditComposer({
  product,
  shop,
  productTypes,
  otherBumpPercentage,
  otherImageRequirements,
  existingProducts,
  isAdmin,
  user,
  livestreamWarning,
  appearInstantly,
  onClose,
}) {
  const [state, dispatch] = React.useReducer(
    composerReducer,
    { shop, product },
    createEditComposer,
  );
  const [attempted, setAttempted] = React.useState(false);
  const [revealToken, setRevealToken] = React.useState(0);
  const [confirming, setConfirming] = React.useState(null);
  const [isSaving, setSaving] = React.useState(false);
  const [server, setServer] = React.useState({ problems: [], message: null });
  const notify = useNotification();
  const closing = useComposerClose({ isDirty: state.isDirty, onClose });

  const [card] = state.cards;
  const [signedType] = state.signedTypes;
  const readOnly = isReadOnlyFor({ state, isAdmin });
  const productType = productTypes.find(
    (t) => t.productTypeId === state.productTypeId,
  );
  const { bumpFacts, aspectRatio, requirements } = productFacts({
    product,
    productType,
    settings: state.settings,
    otherBumpPercentage,
    otherImageRequirements,
  });
  const problems = editProblems({
    state,
    productTypes,
    existingProducts,
    user,
  });
  const bumpControls = fixedBumpControlsFor({
    user,
    stored: product,
    current: { signedType: product.signedType, ...state.settings },
  });
  const uploader = useBatchUploader({
    dispatch,
    productTypeId: state.productTypeId,
    aspectRatio,
    requirements,
  });
  const visibleSignerIds = isAdmin
    ? state.signers.map((s) => s.influencerId)
    : [shop.influencerId];

  const save = async (settings = state.settings) => {
    setSaving(true);
    const result = await updateProduct({
      productId: state.productId,
      body: toUpdateRequest({ ...state, settings }),
    });
    setSaving(false);

    if (result.ok) {
      notify({ message: "Product saved", type: "success" });
      closing.close({ shouldRefreshInfluencer: true });
    } else {
      setServer({ problems: result.problems, message: result.message });
      setRevealToken((token) => token + 1);
    }
  };

  const refuse = () => {
    setAttempted(true);
    setRevealToken((token) => token + 1);
  };

  const requestSave = () => {
    const zeroSplits = zeroSplitsOf(state);

    return problems.length > 0
      ? refuse()
      : zeroSplits.length > 0
      ? setConfirming({ kind: "zeroSplit", detail: zeroSplits })
      : save();
  };

  const saveButton = (
    <Button
      variant="filled"
      loading={isSaving}
      disabled={readOnly || !state.isDirty}
      onClick={requestSave}
    >
      Save
    </Button>
  );

  return (
    <>
      <ComposerShell
        open={closing.open}
        onRequestClose={closing.requestClose}
        onClosed={closing.onClosed}
        title="Edit product"
        subtitle={`${SignType.toLabel(signedType)} · ${
          productType?.name ?? product.productType ?? "Other"
        }`}
        revealToken={revealToken}
        appearInstantly={appearInstantly}
        footer={saveButton}
      >
        {readOnly && (
          <Alert severity="info">
            Only admins can change products with more than one signer.
          </Alert>
        )}
        {livestreamWarning && signedType === SignType.livestream && (
          <Alert severity="warning" title="Not visible yet">
            This product won&apos;t be shown to customers until a livestream
            event date has been set.
          </Alert>
        )}
        {attempted && <ProblemList problems={problems} title="Before saving" />}
        <ProblemList
          problems={server.problems}
          title="The server couldn't save this product"
        />
        {server.message && server.problems.length === 0 && (
          <Alert severity="error">{server.message}</Alert>
        )}

        <ImagesSection
          card={card}
          dispatch={dispatch}
          uploader={uploader}
          problems={problems}
          showErrors={attempted}
          readOnly={readOnly}
        />
        <DetailsSection
          card={card}
          problems={problems}
          showErrors={attempted}
          readOnly={readOnly}
          onChange={(changes) =>
            dispatch({ type: "updateCard", cardId: card.cardId, changes })
          }
        />
        <SignTypePricing
          state={state}
          dispatch={dispatch}
          signedTypes={state.signedTypes}
          bumpFacts={bumpFacts}
          visibleSignerIds={visibleSignerIds}
          canManageSigners={isAdmin}
          readOnly={readOnly}
        />
        <TagsSection
          state={state}
          dispatch={dispatch}
          isAdmin={isAdmin}
          readOnly={readOnly}
        />
        <ServicesSection
          state={state}
          dispatch={dispatch}
          visibleSignerIds={visibleSignerIds}
          readOnly={readOnly}
        />
        <div className={css.advanced}>
          <AdvancedSection
            settings={state.settings}
            isAdmin={isAdmin}
            bumpControls={bumpControls}
            readOnly={readOnly}
            onChange={(key, value) =>
              dispatch({ type: "setSetting", key, value })
            }
          />
        </div>

        {isAdmin && (
          <section className={css.danger}>
            <SectionHead title="Delete product" as="h3" />
            <Typography variant="body-md" color="variant" as="p">
              Removes the product from the shop and search. Existing orders are
              not affected.
            </Typography>
            <Button
              variant="outlined"
              color="error"
              onClick={() => setConfirming({ kind: "delete" })}
            >
              Delete product
            </Button>
          </section>
        )}
      </ComposerShell>

      <ConfirmDialog
        open={confirming?.kind === "zeroSplit"}
        title="Save with a $0 split?"
        description={`These signers would earn nothing: ${(
          confirming?.detail ?? []
        ).join(
          ", ",
        )}. This is usually only right when an agent takes the whole payout.`}
        confirmLabel="Save"
        onConfirm={() => {
          setConfirming(null);
          save();
        }}
        onCancel={() => setConfirming(null)}
      />

      <ConfirmDialog
        open={confirming?.kind === "delete"}
        title="Delete this product?"
        description="It will no longer appear in the shop builder, on the shop or in search."
        confirmLabel="Delete"
        destructive
        onConfirm={() => {
          setConfirming(null);
          save({ ...state.settings, archived: true, active: false });
        }}
        onCancel={() => setConfirming(null)}
      />

      {uploader.editingSource && (
        <ImageEditor
          source={uploader.editingSource}
          aspectRatio={aspectRatio}
          requirements={requirements}
          onCancel={uploader.cancelEdit}
          onSave={uploader.saveEdit}
        />
      )}

      <UploadStatus cards={state.cards} />

      <DiscardDialog {...closing.discard} />
    </>
  );
}

EditComposer.propTypes = {
  existingProducts: PropTypes.arrayOf(PropTypes.object).isRequired,
  appearInstantly: PropTypes.bool,
  isAdmin: PropTypes.bool,
  livestreamWarning: PropTypes.bool,
  onClose: PropTypes.func,
  otherBumpPercentage: PropTypes.number,
  otherImageRequirements: PropTypes.object,
  product: PropTypes.object.isRequired,
  productTypes: PropTypes.arrayOf(PropTypes.object).isRequired,
  shop: PropTypes.object.isRequired,
  user: PropTypes.shape({
    isAdmin: PropTypes.bool,
    level: PropTypes.string,
    permissions: PropTypes.arrayOf(PropTypes.string),
  }).isRequired,
};
